import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiArrowLeft, FiPhone, FiMail, FiGlobe, FiMapPin,
  FiUser, FiCalendar, FiClock, FiExternalLink, FiCheckCircle,
  FiXCircle, FiAlertCircle, FiBriefcase, FiUsers, FiTag,
} from "react-icons/fi";

const API = "https://estatebackend.pixelmindsolutions.com";

/* ─── Styles ─── */
const StyleTag = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');

    :root {
      --red-from:   #ef4444;
      --red-to:     #dc2626;
      --red-soft:   rgba(239,68,68,0.07);
      --red-border: rgba(239,68,68,0.18);
      --bg:         #f8f7f5;
      --surface:    #ffffff;
      --border:     #ede9e3;
      --ink:        #1a1410;
      --muted:      #8a8278;
      --shadow-sm:  0 1px 3px rgba(0,0,0,0.06),0 1px 2px rgba(0,0,0,0.04);
      --shadow-md:  0 4px 16px rgba(0,0,0,0.08),0 2px 6px rgba(0,0,0,0.04);
      --shadow-lg:  0 20px 56px rgba(0,0,0,0.1),0 4px 14px rgba(0,0,0,0.06);
    }

    .pd-root {
      min-height: 100vh;
      background: var(--bg);
      font-family: 'DM Sans', sans-serif;
      color: var(--ink);
      padding-bottom: 80px;
    }

    .pd-center {
      min-height: 100vh;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      background: var(--bg);
      font-family: 'DM Sans', sans-serif;
      gap: 14px;
    }
    @keyframes pdspin { to { transform: rotate(360deg); } }
    .pd-spinner {
      width: 36px; height: 36px;
      border: 3px solid var(--red-soft);
      border-top-color: var(--red-from);
      border-radius: 50%;
      animation: pdspin 0.8s linear infinite;
    }
    .pd-loading-text { font-size: 13px; color: var(--muted); }

    /* ── Hero ── */
    .pd-hero {
      position: relative;
      height: clamp(240px, 38vw, 420px);
      background: #e5e0d8;
      overflow: hidden;
    }
    .pd-hero-img {
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.5s ease;
    }
    .pd-hero:hover .pd-hero-img { transform: scale(1.03); }
    .pd-hero-overlay {
      position: absolute; inset: 0;
      background: linear-gradient(to top, rgba(26,20,16,0.88) 0%, rgba(26,20,16,0.08) 55%, transparent 100%);
    }
    .pd-hero-content {
      position: absolute;
      bottom: 0; left: 0; right: 0;
      padding: clamp(20px, 4vw, 40px);
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }
    .pd-hero-name {
      font-family: 'Playfair Display', serif;
      font-size: clamp(1.5rem, 4vw, 2.6rem);
      font-weight: 600;
      color: #fff;
      line-height: 1.1;
      text-shadow: 0 2px 12px rgba(0,0,0,0.3);
    }
    .pd-hero-cat {
      font-size: 12px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.6);
      margin-top: 6px;
    }
    .pd-status-badge {
      display: flex; align-items: center; gap: 6px;
      padding: 7px 14px;
      border-radius: 100px;
      font-size: 12px; font-weight: 600;
      backdrop-filter: blur(8px);
      white-space: nowrap;
    }
    .pd-status-approved { background: rgba(22,163,74,0.22);  color: #bbf7d0; border: 1px solid rgba(22,163,74,0.4); }
    .pd-status-pending  { background: rgba(234,179,8,0.22);  color: #fef08a; border: 1px solid rgba(234,179,8,0.4); }
    .pd-status-rejected { background: rgba(239,68,68,0.22);  color: #fecaca; border: 1px solid rgba(239,68,68,0.4); }

    .pd-back {
      position: absolute; top: 16px; left: 16px; z-index: 10;
      display: flex; align-items: center; gap: 6px;
      padding: 8px 14px;
      border-radius: 100px; border: none;
      background: rgba(255,255,255,0.15);
      backdrop-filter: blur(8px);
      color: #fff;
      font-family: 'DM Sans', sans-serif;
      font-size: 12px; font-weight: 500;
      cursor: pointer;
      transition: background 0.15s;
    }
    .pd-back:hover { background: rgba(255,255,255,0.25); }

    /* Thumbnail strip */
    .pd-thumbs {
      display: flex; gap: 8px;
      padding: 14px clamp(16px, 4vw, 40px);
      overflow-x: auto; scrollbar-width: none;
      background: var(--surface);
      border-bottom: 1px solid var(--border);
    }
    .pd-thumbs::-webkit-scrollbar { display: none; }
    .pd-thumb-btn {
      flex-shrink: 0; width: 60px; height: 44px;
      border-radius: 8px; overflow: hidden;
      border: 2px solid transparent;
      cursor: pointer; background: none; padding: 0;
      transition: border-color 0.15s, transform 0.15s;
    }
    .pd-thumb-btn.active { border-color: var(--red-from); transform: scale(1.05); }
    .pd-thumb-btn:hover:not(.active) { border-color: #d0c8be; }
    .pd-thumb-img { width: 100%; height: 100%; object-fit: cover; display: block; }

    /* ── Body layout ── */
    .pd-body {
      max-width: 1100px;
      margin: 0 auto;
      padding: clamp(20px, 4vw, 40px);
      display: grid;
      grid-template-columns: 1fr 300px;
      gap: 24px;
      align-items: start;
    }
    @media (max-width: 860px) {
      .pd-body { grid-template-columns: 1fr; }
    }

    /* ── Sections ── */
    .pd-section {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 18px;
      overflow: hidden;
      margin-bottom: 20px;
      box-shadow: var(--shadow-sm);
    }
    .pd-section-header {
      display: flex; align-items: center; gap: 10px;
      padding: 14px 20px;
      border-bottom: 1px solid var(--border);
      background: var(--bg);
    }
    .pd-section-icon {
      width: 30px; height: 30px;
      border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .pd-section-title {
      font-family: 'Playfair Display', serif;
      font-size: 1rem; font-weight: 600;
      color: var(--ink);
    }
    .pd-section-body { padding: 18px 20px; }

    .pd-description { font-size: 14px; line-height: 1.78; color: #4a4540; }

    /* Attributes */
    .attr-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
      gap: 10px;
    }
    .attr-item {
      background: var(--bg);
      border: 1px solid var(--border);
      border-radius: 11px;
      padding: 11px 13px;
    }
    .attr-label { font-size: 10px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); margin-bottom: 3px; }
    .attr-val { font-size: 13px; font-weight: 500; color: var(--ink); }
    .attr-bool-y { color: #15803d; }
    .attr-bool-n { color: var(--muted); }

    /* Portfolio */
    .portfolio-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 14px;
    }
    .portfolio-card {
      background: var(--bg);
      border: 1px solid var(--border);
      border-radius: 14px;
      overflow: hidden;
      transition: transform 0.22s cubic-bezier(.22,.68,0,1.1), box-shadow 0.22s;
    }
    .portfolio-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-md); }
    .portfolio-logo-wrap {
      height: 88px; background: #eae5de;
      display: flex; align-items: center; justify-content: center;
      overflow: hidden;
    }
    .portfolio-logo { width: 100%; height: 100%; object-fit: cover; }
    .portfolio-logo-fallback {
      width: 46px; height: 46px;
      border-radius: 12px;
      background: var(--red-soft);
      border: 1px solid var(--red-border);
      display: flex; align-items: center; justify-content: center;
      color: var(--red-from);
      font-family: 'Playfair Display', serif;
      font-size: 1.1rem; font-weight: 600;
    }
    .portfolio-body { padding: 11px 12px; }
    .portfolio-name { font-weight: 600; font-size: 13px; color: var(--ink); margin-bottom: 4px; }
    .portfolio-desc {
      font-size: 11px; color: var(--muted); line-height: 1.5;
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
      overflow: hidden; margin-bottom: 8px;
    }
    .portfolio-links { display: flex; gap: 5px; flex-wrap: wrap; }
    .portfolio-link {
      display: flex; align-items: center; gap: 3px;
      padding: 3px 7px; border-radius: 6px;
      font-size: 10px; font-weight: 500;
      text-decoration: none; transition: background 0.12s;
    }
    .pl-web   { background: rgba(8,145,178,0.08); color: #0891b2; border: 1px solid rgba(8,145,178,0.2); }
    .pl-web:hover  { background: rgba(8,145,178,0.14); }
    .pl-play  { background: rgba(22,163,74,0.08); color: #15803d; border: 1px solid rgba(22,163,74,0.2); }
    .pl-play:hover { background: rgba(22,163,74,0.14); }
    .pl-ios   { background: rgba(100,100,100,0.07); color: #555; border: 1px solid rgba(0,0,0,0.1); }
    .pl-ios:hover  { background: rgba(100,100,100,0.13); }

    /* Rejection banner */
    .rejection-banner {
      display: flex; gap: 10px;
      padding: 13px 18px;
      background: rgba(239,68,68,0.06);
      border: 1px solid var(--red-border);
      border-radius: 12px;
      margin-bottom: 20px;
    }
    .rejection-text { font-size: 13px; color: var(--ink); line-height: 1.5; }
    .rejection-text strong { color: var(--red-to); }

    /* ── Sidebar ── */
    .contact-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 18px;
      overflow: hidden;
      margin-bottom: 16px;
      box-shadow: var(--shadow-sm);
    }
    .contact-top-bar { height: 3px; background: linear-gradient(90deg, var(--red-from), var(--red-to)); }
    .contact-row {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 16px;
      border-bottom: 1px solid var(--border);
      transition: background 0.12s;
    }
    .contact-row:last-child { border-bottom: none; }
    .contact-row:hover { background: var(--red-soft); }
    .cri {
      width: 32px; height: 32px;
      border-radius: 9px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .cr-label { font-size: 10px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); }
    .cr-val { font-size: 13px; font-weight: 500; color: var(--ink); text-decoration: none; word-break: break-all; }
    a.cr-val:hover { color: var(--red-from); }

    .map-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 18px;
      overflow: hidden;
      margin-bottom: 16px;
      box-shadow: var(--shadow-sm);
    }
    .map-visual {
      height: 120px;
      background: linear-gradient(135deg, #e0f2ee, #d4ede5);
      display: flex; align-items: center; justify-content: center;
    }
    .map-pin-circle {
      width: 44px; height: 44px; border-radius: 50%;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      display: flex; align-items: center; justify-content: center;
      color: #fff;
      box-shadow: 0 6px 20px rgba(239,68,68,0.4), 0 0 0 10px rgba(239,68,68,0.1);
    }
    .map-body { padding: 14px 16px; }
    .map-address { font-size: 13px; color: var(--ink); line-height: 1.55; margin-bottom: 10px; }
    .map-btn {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 8px 14px;
      border-radius: 9px;
      background: var(--red-soft);
      border: 1px solid var(--red-border);
      color: var(--red-to);
      font-size: 12px; font-weight: 500;
      text-decoration: none;
      transition: background 0.15s;
    }
    .map-btn:hover { background: rgba(239,68,68,0.13); }

    .meta-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 18px;
      padding: 14px 16px;
      box-shadow: var(--shadow-sm);
    }
    .meta-row {
      display: flex; align-items: center; gap: 8px;
      padding: 7px 0; border-bottom: 1px solid var(--border);
      font-size: 12px;
    }
    .meta-row:last-child { border-bottom: none; }
    .meta-key { color: var(--muted); flex: 1; }
    .meta-val { color: var(--ink); font-weight: 500; text-align: right; }

    @media (max-width: 480px) {
      .pd-hero-content { flex-direction: column; align-items: flex-start; }
      .attr-grid { grid-template-columns: 1fr 1fr; }
      .portfolio-grid { grid-template-columns: 1fr; }
    }
  `}</style>
);

/* ── status helper ── */
const statusInfo = (s) => ({
  approved: { cls: "pd-status-approved", icon: <FiCheckCircle size={12} />, label: "Approved" },
  pending:  { cls: "pd-status-pending",  icon: <FiClock size={12} />,       label: "Pending"  },
  rejected: { cls: "pd-status-rejected", icon: <FiXCircle size={12} />,     label: "Rejected" },
}[s] || { cls: "pd-status-pending", icon: <FiClock size={12} />, label: "Pending" });

/* ═══════════════════════════════════════ */
const ProductDetail = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/${id}`);
        setProduct(res.data.product);
      } catch {
        setError("Failed to load product details.");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetch();
  }, [id]);

  if (loading) return (
    <><StyleTag />
      <div className="pd-center">
        <div className="pd-spinner" />
        <span className="pd-loading-text">Loading details…</span>
      </div>
    </>
  );

  if (error || !product) return (
    <><StyleTag />
      <div className="pd-center">
        <div style={{ width: 52, height: 52, borderRadius: 14, background: "var(--red-soft)", border: "1px solid var(--red-border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--red-from)" }}>
          <FiAlertCircle size={22} />
        </div>
        <span style={{ fontSize: 13, color: "var(--muted)" }}>{error || "Product not found."}</span>
        <button onClick={() => navigate(-1)} style={{ padding: "8px 18px", borderRadius: 10, border: "1px solid var(--border)", background: "#fff", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          ← Go Back
        </button>
      </div>
    </>
  );

  const p   = product;
  const s   = p.status || (p.isApproved ? "approved" : "pending");
  const si  = statusInfo(s);
  const lat = p.location?.coordinates?.[1];
  const lng = p.location?.coordinates?.[0];

  const renderAttrs = () =>
    Object.entries(p.attributes || {})
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([key, val]) => {
        const label   = key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
        const isBool  = typeof val === "boolean";
        const display = isBool ? (val ? "✓ Yes" : "✗ No") : Array.isArray(val) ? val.join(", ") : String(val);
        return (
          <div className="attr-item" key={key}>
            <div className="attr-label">{label}</div>
            <div className={`attr-val ${isBool ? (val ? "attr-bool-y" : "attr-bool-n") : ""}`}>{display}</div>
          </div>
        );
      });

  return (
    <><StyleTag />
    <div className="pd-root">

      {/* HERO */}
      <div className="pd-hero">
        {p.images?.[activeImg] && (
          <img className="pd-hero-img" src={p.images[activeImg]} alt={p.name}
            onError={(e) => { e.target.src = "https://placehold.co/1100x420"; }} />
        )}
        <div className="pd-hero-overlay" />
        <button className="pd-back" onClick={() => navigate(-1)}>
          <FiArrowLeft size={13} /> Back
        </button>
        <div className="pd-hero-content">
          <div>
            <div className="pd-hero-name">{p.name}</div>
            <div className="pd-hero-cat">{p.category?.name}</div>
          </div>
          <div className={`pd-status-badge ${si.cls}`}>{si.icon} {si.label}</div>
        </div>
      </div>

      {/* THUMBNAILS */}
      {p.images?.length > 1 && (
        <div className="pd-thumbs">
          {p.images.map((img, i) => (
            <button key={i} className={`pd-thumb-btn ${i === activeImg ? "active" : ""}`} onClick={() => setActiveImg(i)}>
              <img className="pd-thumb-img" src={img} alt="" onError={(e) => { e.target.src = "https://placehold.co/60x44"; }} />
            </button>
          ))}
        </div>
      )}

      {/* BODY */}
      <div className="pd-body">

        {/* Main */}
        <div>
          {p.rejectionReason && (
            <div className="rejection-banner">
              <FiAlertCircle size={16} style={{ color: "var(--red-from)", flexShrink: 0, marginTop: 2 }} />
              <div className="rejection-text"><strong>Rejection reason:</strong> {p.rejectionReason}</div>
            </div>
          )}

          {/* About */}
          {p.description && (
            <div className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-icon" style={{ background: "rgba(37,99,235,0.08)", color: "#2563eb" }}><FiUser size={13} /></div>
                <span className="pd-section-title">About</span>
              </div>
              <div className="pd-section-body">
                <p className="pd-description">{p.description}</p>
              </div>
            </div>
          )}

          {/* Attributes */}
          {p.attributes && Object.keys(p.attributes).length > 0 && (
            <div className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-icon" style={{ background: "rgba(124,58,237,0.08)", color: "#7c3aed" }}><FiTag size={13} /></div>
                <span className="pd-section-title">Details & Attributes</span>
              </div>
              <div className="pd-section-body">
                <div className="attr-grid">{renderAttrs()}</div>
              </div>
            </div>
          )}

          {/* Portfolio */}
          {p.portfolio?.length > 0 && (
            <div className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-icon" style={{ background: "rgba(8,145,178,0.08)", color: "#0891b2" }}><FiBriefcase size={13} /></div>
                <span className="pd-section-title">Portfolio</span>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted)", background: "#f0ece6", padding: "2px 8px", borderRadius: 100 }}>
                  {p.portfolio.length} projects
                </span>
              </div>
              <div className="pd-section-body">
                <div className="portfolio-grid">
                  {p.portfolio.map((item) => (
                    <div className="portfolio-card" key={item._id}>
                      <div className="portfolio-logo-wrap">
                        {item.logo
                          ? <img className="portfolio-logo" src={item.logo} alt={item.name} onError={(e) => { e.target.style.display = "none"; }} />
                          : <div className="portfolio-logo-fallback">{item.name?.charAt(0) || "?"}</div>
                        }
                      </div>
                      <div className="portfolio-body">
                        <div className="portfolio-name">{item.name}</div>
                        {item.description && <div className="portfolio-desc">{item.description}</div>}
                        <div className="portfolio-links">
                          {item.website && (
                            <a href={item.website.startsWith("http") ? item.website : `https://${item.website}`}
                              target="_blank" rel="noopener noreferrer" className="portfolio-link pl-web">
                              <FiGlobe size={9} /> Web
                            </a>
                          )}
                          {item.playStoreLink && (
                            <a href={item.playStoreLink} target="_blank" rel="noopener noreferrer" className="portfolio-link pl-play">
                              <FiExternalLink size={9} /> Play
                            </a>
                          )}
                          {item.appStoreLink && (
                            <a href={item.appStoreLink} target="_blank" rel="noopener noreferrer" className="portfolio-link pl-ios">
                              <FiExternalLink size={9} /> iOS
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Owner */}
          {p.user && (
            <div className="pd-section">
              <div className="pd-section-header">
                <div className="pd-section-icon" style={{ background: "rgba(22,163,74,0.08)", color: "#15803d" }}><FiUsers size={13} /></div>
                <span className="pd-section-title">Owner</span>
              </div>
              <div className="pd-section-body" style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--red-soft)", border: "1px solid var(--red-border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--red-from)", flexShrink: 0 }}>
                  <FiUser size={18} />
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.user.name || "—"}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{p.user.email}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{p.user.mobile}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Contact */}
          {(p.contact?.callNumber || p.contact?.email || p.contact?.website || p.contact?.whatsappNumber) && (
            <div className="contact-card">
              <div className="contact-top-bar" />
              {p.contact?.callNumber && (
                <div className="contact-row">
                  <div className="cri" style={{ background: "rgba(37,99,235,0.08)", color: "#2563eb" }}><FiPhone size={13} /></div>
                  <div><div className="cr-label">Call</div><a href={`tel:${p.contact.callNumber}`} className="cr-val">{p.contact.callNumber}</a></div>
                </div>
              )}
              {p.contact?.whatsappNumber && (
                <div className="contact-row">
                  <div className="cri" style={{ background: "rgba(22,163,74,0.08)", color: "#15803d" }}><FiPhone size={13} /></div>
                  <div><div className="cr-label">WhatsApp</div><a href={`https://wa.me/${p.contact.whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="cr-val">{p.contact.whatsappNumber}</a></div>
                </div>
              )}
              {p.contact?.email && (
                <div className="contact-row">
                  <div className="cri" style={{ background: "rgba(124,58,237,0.08)", color: "#7c3aed" }}><FiMail size={13} /></div>
                  <div><div className="cr-label">Email</div><a href={`mailto:${p.contact.email}`} className="cr-val">{p.contact.email}</a></div>
                </div>
              )}
              {p.contact?.website && (
                <div className="contact-row">
                  <div className="cri" style={{ background: "rgba(8,145,178,0.08)", color: "#0891b2" }}><FiGlobe size={13} /></div>
                  <div>
                    <div className="cr-label">Website</div>
                    <a href={p.contact.website.startsWith("http") ? p.contact.website : `https://${p.contact.website}`}
                      target="_blank" rel="noopener noreferrer" className="cr-val">
                      {p.contact.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Location */}
          {(p.address || (lat && lng)) && (
            <div className="map-card">
              <div className="map-visual">
                <div className="map-pin-circle"><FiMapPin size={20} /></div>
              </div>
              <div className="map-body">
                {p.address && <div className="map-address">{p.address}</div>}
                {lat && lng && (
                  <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer" className="map-btn">
                    <FiMapPin size={11} /> Open in Google Maps
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Meta */}
          <div className="meta-card">
            <div className="meta-row">
              <FiCalendar size={11} style={{ color: "var(--muted)" }} />
              <span className="meta-key">Created</span>
              <span className="meta-val">{new Date(p.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
            </div>
            <div className="meta-row">
              <FiCalendar size={11} style={{ color: "var(--muted)" }} />
              <span className="meta-key">Updated</span>
              <span className="meta-val">{new Date(p.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
            </div>
            <div className="meta-row">
              <span className="meta-key">Active</span>
              <span className="meta-val" style={{ color: p.isActive ? "#15803d" : "var(--muted)" }}>{p.isActive ? "✓ Yes" : "✗ No"}</span>
            </div>
            <div className="meta-row">
              <span className="meta-key">ID</span>
              <span className="meta-val" style={{ fontFamily: "monospace", fontSize: 11, color: "var(--muted)" }}>…{p._id.slice(-8)}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default ProductDetail;